import { getPaymentSummary } from "@/services/masters";
import React, {
  useState,
  useEffect,
  useCallback,
  CSSProperties,
} from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Search } from "lucide-react";
import Pagination from "../common/Pagination";

const T = {
  ink: "#16232F",
  inkSoft: "#5B6B78",
  inkFaint: "#94A2AC",
  paper: "#F3F5F4",
  paperRaised: "#FFFFFF",
  line: "#E4E8E6",
  paid: "#1F9D74",
  paidSoft: "#E4F5EE",
  pending: "#DE8F3F",
  pendingSoft: "#FCEFDF",
  violet: "#6D5DD3",
  violetSoft: "#E7E3FA",
  danger: "#C1503F",
};

interface ChildPaid {
  Child_Id: number;
  Child_Name: string;
  Amount: number;
  Payment_Date: string | null;
  Child_Status: string;
  Renewal_Count: number;
}

interface ChildPending {
  Child_Id: number;
  Child_Name: string;
  Amount: number;
  Created_At: string | null;
}

interface ParentGroup<TChild> {
  Username: string;
  Total_Amount: number;
  Total_Children: number;
  Children: TChild[];
}

interface PaymentSummary {
  Total_Amount: number;
  Paid_Amount: number;
  Pending_Amount: number;
  Total_Children: number;
  Paid_Children: number;
  Pending_Children: number;
  Total_Parents: number;
  Paid_Parents: number;
  Pending_Parents: number;
  Paid_Details: ParentGroup<ChildPaid>[];
  Pending_Details: ParentGroup<ChildPending>[];
}

const PAGE_SIZE_DEFAULT = 20;

const money = (n: number | undefined) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const compactMoney = (n: number | undefined) => {
  const v = Number(n || 0);

  if (v >= 100000) {
    return (
      "₹" +
      (v / 100000)
        .toFixed(1)
        .replace(/\.0$/, "") +
      "L"
    );
  }

  if (v >= 1000) {
    return (
      "₹" +
      (v / 1000)
        .toFixed(1)
        .replace(/\.0$/, "") +
      "k"
    );
  }

  return money(v);
};

const pad2 = (n: number) =>
  String(n).padStart(2, "0");

const fmtDate = (
  s: string | null | undefined
) => {
  if (!s) return "—";

  try {
    const d = new Date(s);

    if (isNaN(d.getTime())) {
      return s;
    }

    const dd = pad2(d.getDate());
    const mm = pad2(d.getMonth() + 1);
    const yy = pad2(d.getFullYear() % 100);

    return `${dd}/${mm}/${yy}`;
  } catch {
    return s;
  }
};

const tdStyle: CSSProperties = {
  fontSize: 13,
  color: T.ink,
  padding: "10px 4px",
};

const inputStyle: CSSProperties = {
  border: `1px solid ${T.line}`,
  borderRadius: 9,
  padding: "8px 11px",
  fontSize: 13,
  color: T.ink,
  background: T.paperRaised,
  minWidth: 112,
  outline: "none",
};

const btnStyle = (
  disabled: boolean
): CSSProperties => ({
  border: `1px solid ${T.line}`,
  background: disabled
    ? T.paper
    : T.paperRaised,
  color: disabled
    ? T.inkFaint
    : T.ink,
  borderRadius: 9,
  padding: "7px 14px",
  fontSize: 13,
  cursor: disabled
    ? "not-allowed"
    : "pointer",
  fontWeight: 600,
  transition: "background 0.15s ease",
});

function Card({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: T.paperRaised,
        border: `1px solid ${T.line}`,
        borderRadius: 16,
        boxShadow:
          "0 1px 2px rgba(22,35,47,0.04)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function StatPill({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 140,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 99,
            background: accent,
            flexShrink: 0,
          }}
        />

        <span
          style={{
            fontSize: 11,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: T.inkSoft,
          }}
        >
          {label}
        </span>
      </div>

      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: T.ink,
        }}
      >
        {value}
      </div>

      {sub && (
        <div
          style={{
            fontSize: 11.5,
            color: T.inkFaint,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

interface DonutBlockProps {
  title: string;
  paid: number;
  pending: number;
  total: number;
  formatValue?: (n: number) => string;
}

function DonutBlock({
  title,
  paid,
  pending,
  total,
  formatValue = (n) => String(n),
}: DonutBlockProps) {
  const data = [
    {
      name: "Paid",
      value: paid,
      color: T.paid,
    },
    {
      name: "Pending",
      value: pending,
      color: T.pending,
    },
  ];

  const pct = total
    ? Math.round((paid / total) * 100)
    : 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: "1 1 200px",
        minWidth: 180,
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: T.inkSoft,
          marginBottom: 6,
        }}
      >
        {title}
      </div>

      <div
        style={{
          position: "relative",
          width: 148,
          height: 148,
        }}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="68%"
              outerRadius="94%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={total ? 3 : 0}
              stroke="none"
            >
              {data.map((d, i) => (
                <Cell
                  key={i}
                  fill={
                    total ? d.color : T.line
                  }
                />
              ))}
            </Pie>

            <Tooltip
              formatter={(
                v: number,
                n: string
              ) => [
                formatValue(v),
                n,
              ]}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: `1px solid ${T.line}`,
                boxShadow:
                  "0 4px 12px rgba(22,35,47,0.08)",
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: T.ink,
            }}
          >
            {pct}%
          </div>

          <div
            style={{
              fontSize: 9.5,
              color: T.inkFaint,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            paid
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 14,
          marginTop: 10,
        }}
      >
        <LegendItem
          color={T.paid}
          label="Paid"
          value={formatValue(paid)}
        />

        <LegendItem
          color={T.pending}
          label="Pending"
          value={formatValue(pending)}
        />
      </div>
    </div>
  );
}

function LegendItem({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 99,
          background: color,
          flexShrink: 0,
        }}
      />

      <div
        style={{
          fontSize: 11.5,
          color: T.inkSoft,
          lineHeight: 1.3,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            color: T.ink,
            fontSize: 12,
          }}
        >
          {value}
        </div>

        <div>{label}</div>
      </div>
    </div>
  );
}

function TabBar({
  tab,
  onChange,
  paidCount,
  pendingCount,
}: {
  tab: "paid" | "pending";
  onChange: (
    t: "paid" | "pending"
  ) => void;
  paidCount: number;
  pendingCount: number;
}) {
  const segment = (
    key: "paid" | "pending",
    label: string,
    count: number,
    accent: string,
    accentSoft: string
  ) => {
    const active = tab === key;

    return (
      <button
        onClick={() => onChange(key)}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          border: "none",
          borderRadius: 9,
          background: active
            ? T.paperRaised
            : "transparent",
          boxShadow: active
            ? "0 1px 3px rgba(22,35,47,0.10)"
            : "none",
          padding: "10px 12px",
          cursor: "pointer",
          transition:
            "background 0.15s ease, box-shadow 0.15s ease",
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: active
              ? T.ink
              : T.inkSoft,
          }}
        >
          {label}
        </span>

        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: "1px 8px",
            borderRadius: 99,
            background: active
              ? accentSoft
              : T.paperRaised,
            color: active
              ? accent
              : T.inkFaint,
          }}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        background: T.paper,
        borderRadius: 11,
        padding: 4,
        width: "100%",
      }}
    >
      {segment(
        "paid",
        "Paid",
        paidCount,
        T.paid,
        T.paidSoft
      )}

      {segment(
        "pending",
        "Pending",
        pendingCount,
        T.pending,
        T.pendingSoft
      )}
    </div>
  );
}

interface DetailsTableProps {
  kind: "paid" | "pending";
  rows: ParentGroup<
    ChildPaid | ChildPending
  >[];
  page: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

function DetailsTable({
  kind,
  rows,
  page,
  totalCount,
  pageSize,
  onPageChange,
}: DetailsTableProps) {
  const [openRow, setOpenRow] =
    useState<string | null>(null);

  const isPaid = kind === "paid";

  const accent = isPaid
    ? T.paid
    : T.pending;

  const accentSoft = isPaid
    ? T.paidSoft
    : T.pendingSoft;

  const totalPages = Math.ceil(
    totalCount / pageSize
  );

  return (
    <div style={{ width: "100%" }}>
            {totalPages > 1 && (
        <div style={{ marginTop: 16 }}>
          <Pagination
            itemsPerPage={pageSize}
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      )}
      {rows.length === 0 && (
        <div
          style={{
            fontSize: 13,
            color: T.inkSoft,
            padding: "36px 0",
            textAlign: "center",
          }}
        >
          No{" "}
          {isPaid ? "paid" : "pending"}{" "}
          records for this filter.
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginTop: 14,
          width: "100%",
        }}
      >
        {rows.map((row, idx) => {
          const key = `${row.Username}-${idx}`;
          const open = openRow === key;

          return (
            <div
              key={key}
              style={{
                width: "100%",
                border: `1px solid ${
                  open
                    ? accent + "55"
                    : T.line
                }`,
                borderRadius: 12,
                overflow: "hidden",
                transition:
                  "border-color 0.15s ease",
              }}
            >
              <button
                onClick={() =>
                  setOpenRow(
                    open ? null : key
                  )
                }
                style={{
                  width: "100%",
                  background: open
                    ? T.paper
                    : "transparent",
                  border: "none",
                  padding: "12px 14px",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background:
                        accentSoft,
                      color: accent,
                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      fontWeight: 600,
                      fontSize: 15,
                      flexShrink: 0,
                    }}
                  >
                    {row.Username?.[0]?.toUpperCase() ??
                      "?"}
                  </div>

                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: T.ink,
                      }}
                    >
                      {row.Username}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color: T.inkSoft,
                      }}
                    >
                      {row.Total_Children}{" "}
                      {row.Total_Children === 1
                        ? "child"
                        : "children"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "right",
                    display: "flex",
                    alignItems:
                      "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: accent,
                    }}
                  >
                    {money(
                      row.Total_Amount
                    )}
                  </div>

                  <span
                    style={{
                      display:
                        "inline-block",
                      transform: open
                        ? "rotate(180deg)"
                        : "none",
                      transition:
                        "transform 0.15s ease",
                      color: T.inkFaint,
                      fontSize: 12,
                    }}
                  >
                    ▾
                  </span>
                </div>
              </button>

              {open && (
                <div
                  style={{
                    borderTop: `1px solid ${T.line}`,
                    padding:
                      "6px 14px 12px",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse:
                        "collapse",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>
                          Child
                        </th>

                        <th style={thStyle}>
                          Amount
                        </th>

                        {isPaid ? (
                          <>
                            <th style={thStyle}>
                              Paid on
                            </th>

                            <th style={thStyle}>
                              Status
                            </th>
                          </>
                        ) : (
                          <th style={thStyle}>
                            Created
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {row.Children.map(
                        (c) => {
                          const paidChild =
                            c as ChildPaid;

                          const pendingChild =
                            c as ChildPending;

                          return (
                            <tr
                              key={
                                c.Child_Id
                              }
                              style={{
                                borderTop: `1px solid ${T.line}`,
                              }}
                            >
                              <td
                                style={
                                  tdStyle
                                }
                              >
                                {
                                  c.Child_Name
                                }
                              </td>

                              <td
                                style={
                                  tdStyle
                                }
                              >
                                {money(
                                  c.Amount
                                )}
                              </td>

                              {isPaid ? (
                                <>
                                  <td
                                    style={
                                      tdStyle
                                    }
                                  >
                                    {fmtDate(
                                      paidChild.Payment_Date
                                    )}
                                  </td>

                                  <td
                                    style={
                                      tdStyle
                                    }
                                  >
                                    <span
                                      style={{
                                        fontSize: 11,
                                        padding:
                                          "2px 9px",
                                        borderRadius:
                                          99,
                                        background:
                                          paidChild.Child_Status ===
                                          "Renewal"
                                            ? T.violetSoft
                                            : T.paidSoft,
                                        color:
                                          paidChild.Child_Status ===
                                          "Renewal"
                                            ? T.violet
                                            : T.paid,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {
                                        paidChild.Child_Status
                                      }

                                      {paidChild.Renewal_Count
                                        ? ` ×${paidChild.Renewal_Count}`
                                        : ""}
                                    </span>
                                  </td>
                                </>
                              ) : (
                                <td
                                  style={
                                    tdStyle
                                  }
                                >
                                  {fmtDate(
                                    pendingChild.Created_At
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const thStyle: CSSProperties = {
  textAlign: "left",
  fontSize: 10.5,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: T.inkFaint,
  padding: "8px 4px",
  fontWeight: 600,
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <label
        style={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: T.inkFaint,
        }}
      >
        {label}
      </label>

      {children}
    </div>
  );
}

export default function PaymentSummaryScreen() {
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] =
    useState("");

  const [tab, setTab] =
    useState<"paid" | "pending">(
      "paid"
    );

  const [paidPage, setPaidPage] =
    useState(1);

  const [pendingPage, setPendingPage] =
    useState(1);

  const [pageSize] = useState(
    PAGE_SIZE_DEFAULT
  );

  const [summary, setSummary] =
    useState<PaymentSummary | null>(
      null
    );

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(
        search.trim()
      );

      setPaidPage(1);
      setPendingPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPaidPage(1);
    setPendingPage(1);
  }, [
    year,
    month,
    fromDate,
    toDate,
  ]);

  const fetchSummary =
    useCallback(async () => {
      setLoading(true);
      setError(null);

      try {
        const res: any =
          await getPaymentSummary(
            year
              ? Number(year)
              : undefined,
            month
              ? Number(month)
              : undefined,
            fromDate || undefined,
            toDate || undefined,
            pendingPage,
            paidPage,
            pageSize,
            debouncedSearch
          );

        const json: PaymentSummary =
          res?.data ?? res;

        setSummary(json);
      } catch (e: any) {
        setError(
          e?.message ||
            "Something went wrong"
        );
      } finally {
        setLoading(false);
      }
    }, [
      year,
      month,
      fromDate,
      toDate,
      pendingPage,
      paidPage,
      pageSize,
      debouncedSearch,
    ]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const paidRows =
    summary?.Paid_Details ?? [];

  const pendingRows =
    summary?.Pending_Details ?? [];

  return (
    <div className="max-w-full"
      style={{
        background: T.paper,
        minHeight: "100%",
        // padding:
        //   "28px 24px 60px",
      }}
    >
      <div
        style={{
          // maxWidth: 1080,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 27,
                fontWeight: 700,
                color: T.ink,
              }}
            >
              Payment Ledger
            </div>

            <div
              style={{
                fontSize: 13,
                color: T.inkSoft,
                marginTop: 2,
              }}
            >
              Subscription payments
              across all families
            </div>
          </div>

          <button
            onClick={fetchSummary}
            style={{
              ...btnStyle(false),
              padding: "9px 16px",
            }}
          >
            ⟳ Refresh
          </button>
        </div>

        <Card
          style={{
            padding: "14px 18px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems:
                "flex-end",
            }}
          >
            <Field label="Year">
              <input
                type="number"
                placeholder="2026"
                value={year}
                onChange={(e) =>
                  setYear(e.target.value)
                }
                style={inputStyle}
              />
            </Field>

            <Field label="Month">
              <input
                type="number"
                min={1}
                max={12}
                placeholder="1–12"
                value={month}
                onChange={(e) =>
                  setMonth(e.target.value)
                }
                style={inputStyle}
              />
            </Field>

            <Field label="From date">
              <input
                type="date"
                value={fromDate}
                onChange={(e) =>
                  setFromDate(
                    e.target.value
                  )
                }
                style={inputStyle}
              />
            </Field>

            <Field label="To date">
              <input
                type="date"
                value={toDate}
                onChange={(e) =>
                  setToDate(
                    e.target.value
                  )
                }
                style={inputStyle}
              />
            </Field>

            <Field label="Search">
              <div
                style={{
                  position: "relative",
                }}
              >
                <Search
                  size={15}
                  style={{
                    position:
                      "absolute",
                    left: 10,
                    top: "50%",
                    transform:
                      "translateY(-50%)",
                    color: T.inkFaint,
                  }}
                />

                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    paddingLeft: 32,
                  }}
                />
              </div>
            </Field>

            {(year ||
              month ||
              fromDate ||
              toDate ||
              search) && (
              <button
                onClick={() => {
                  setYear("");
                  setMonth("");
                  setFromDate("");
                  setToDate("");
                  setSearch("");
                }}
                style={{
                  ...btnStyle(false),
                  color: T.danger,
                  borderColor:
                    "#F0CFC7",
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        </Card>

        {error && (
          <div
            style={{
              background:
                T.pendingSoft,
              color: T.danger,
              border:
                "1px solid #F0CFC7",
              borderRadius: 12,
              padding: "11px 15px",
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {loading && !summary ? (
          <Card
            style={{
              padding: 48,
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: T.inkSoft,
                fontSize: 13,
              }}
            >
              Loading ledger…
            </div>
          </Card>
        ) : summary ? (
          <>
            <Card
              style={{
                padding:
                  "22px 24px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 28,
                  flexWrap: "wrap",
                  marginBottom: 22,
                }}
              >
                <StatPill
                  label="Total amount"
                  value={money(
                    summary.Total_Amount
                  )}
                  accent={T.ink}
                />

                <StatPill
                  label="Paid amount"
                  value={money(
                    summary.Paid_Amount
                  )}
                  sub={`${summary.Paid_Parents} parents`}
                  accent={T.paid}
                />

                <StatPill
                  label="Pending amount"
                  value={money(
                    summary.Pending_Amount
                  )}
                  sub={`${summary.Pending_Parents} parents`}
                  accent={T.pending}
                />
              </div>

              <div
                style={{
                  height: 1,
                  background: T.line,
                  marginBottom: 22,
                }}
              />

              <div
                style={{
                  display: "flex",
                  gap: 20,
                  flexWrap: "wrap",
                  justifyContent:
                    "space-around",
                }}
              >
                <DonutBlock
                  title="Amount"
                  paid={
                    summary.Paid_Amount
                  }
                  pending={
                    summary.Pending_Amount
                  }
                  total={
                    summary.Total_Amount
                  }
                  formatValue={
                    compactMoney
                  }
                />

                <DonutBlock
                  title="Children"
                  paid={
                    summary.Paid_Children
                  }
                  pending={
                    summary.Pending_Children
                  }
                  total={
                    summary.Total_Children
                  }
                />

                <DonutBlock
                  title="Parents"
                  paid={
                    summary.Paid_Parents
                  }
                  pending={
                    summary.Pending_Parents
                  }
                  total={
                    summary.Total_Parents
                  }
                />
              </div>
            </Card>

            <Card
              style={{
                padding:
                  "18px 22px 22px",
                width: "100%",
              }}
            >
              <TabBar
                tab={tab}
                onChange={setTab}
                paidCount={
                  summary.Paid_Parents
                }
                pendingCount={
                  summary.Pending_Parents
                }
              />

              {tab === "paid" ? (
                <DetailsTable
                  kind="paid"
                  rows={paidRows}
                  page={paidPage}
                  totalCount={
                    summary.Paid_Parents
                  }
                  pageSize={pageSize}
                  onPageChange={
                    setPaidPage
                  }
                />
              ) : (
                <DetailsTable
                  kind="pending"
                  rows={pendingRows}
                  page={pendingPage}
                  totalCount={
                    summary.Pending_Parents
                  }
                  pageSize={pageSize}
                  onPageChange={
                    setPendingPage
                  }
                />
              )}
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
